import type {
  KeyboardEvent as ReactKeyboardEvent,
  MutableRefObject,
  SetStateAction,
  WheelEvent,
} from 'react';

import type {
  PointerEventModifiers,
  PointerEventPosition,
  RelativeAbsolutePosition,
} from '../../../../types/pointer-events.js';
import { ZOOM_FACTOR } from '../../constants.js';
import type {
  InteractionMode,
  InteractionTriggers,
  ZoomBehavior,
} from '../../types.js';
import { temporaryPan } from '../../utils/temporary-pan.js';
import { temporaryZoom } from '../../utils/temporary-zoom.js';

export const zoomMouseClickOrTap = ({
  event,
  width,
  lastMousePositionRef,
  onZoomChange,
}: {
  event: PointerEventPosition & PointerEventModifiers;
  width: number;
  lastMousePositionRef: MutableRefObject<RelativeAbsolutePosition | undefined>;
  onZoomChange?: (event: {
    triggeredBy: InteractionTriggers;
    deltaStart: number;
    deltaEnd: number;
    behavior: ZoomBehavior;
  }) => void;
}) => {
  const { position } = event;

  const modifier = event.shiftKey ? -1 : 1;
  const ratio = position.relative.x / width;
  const delta = Math.floor(width * ZOOM_FACTOR) * modifier;

  const deltaStart = delta * ratio;
  const deltaEnd = -delta * (1 - ratio);

  onZoomChange?.({
    triggeredBy: 'pointer',
    deltaStart,
    deltaEnd,
    behavior: event.shiftKey ? 'zoom-out' : 'zoom-in',
  });

  lastMousePositionRef.current = position;
};
export const zoomMouseHover = ({
  event,
  pinned,
  lastMousePositionRef,
  cursorMode,
  setCursorMode,
  onHover,
}: {
  event: PointerEventPosition & PointerEventModifiers;
  pinned: boolean;
  lastMousePositionRef: MutableRefObject<RelativeAbsolutePosition | undefined>;
  cursorMode: 'zoomOut' | InteractionMode;
  setCursorMode: (value: SetStateAction<'zoomOut' | InteractionMode>) => void;
  onHover?: (event: {
    triggeredBy: InteractionTriggers;
    position: RelativeAbsolutePosition;
  }) => void;
}) => {
  const { position } = event;
  if (!pinned) {
    onHover?.({ triggeredBy: 'pointer', position });
  }
  lastMousePositionRef.current = position;
  if (event.shiftKey) {
    if (cursorMode !== 'zoomOut') {
      setCursorMode('zoomOut');
    }
  } else if ('zoom' !== cursorMode) {
    setCursorMode('zoom');
  }
};

export const zoomMouseWheel = ({
  event,
  lastMousePositionRef,
  width,
  onZoomChange,
  onPanChange,
}: {
  event: WheelEvent;
  width: number;
  lastMousePositionRef: MutableRefObject<RelativeAbsolutePosition | undefined>;
  onZoomChange?: (event: {
    triggeredBy: InteractionTriggers;
    deltaStart: number;
    deltaEnd: number;
    behavior: ZoomBehavior;
  }) => void;
  onPanChange?: (event: {
    triggeredBy: InteractionTriggers;
    delta: number;
  }) => void;
}) => {
  const isTemporaryPanMode = (event: WheelEvent | ReactKeyboardEvent) =>
    event.altKey;
  const isTemporaryZoomMode = (event: WheelEvent | ReactKeyboardEvent) =>
    event.metaKey || event.ctrlKey;

  if (isTemporaryPanMode(event)) {
    temporaryPan({ deltaX: event.deltaX, onPanChange });
  } else if (isTemporaryZoomMode(event)) {
    temporaryZoom({ event, lastMousePositionRef, width, onZoomChange });
  }
};
